import { JSX } from "react";
import { useTranslation } from "react-i18next";

import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { AddEvent, Event } from "utils/history/Event";
import { ListInfo } from "utils/list/List";
import { asTitle } from "utils/Utils";

import { Competence } from "core/blueprint/competencelist/CompetenceListService";
import CompetenceRow from "core/blueprint/competencelist/CompetenceRow";
import NewCompetenceRow from "core/blueprint/competencelist/NewCompetenceRow";

export type CompetenceTableProps = {
	elements: Competence[];
	listInfo: ListInfo<string>;
	editable: boolean;
	labels?: string[];

	onSelect: (keys: string[], selected: boolean) => void;
	onChange: (event: Event) => void;
	onInsert: (event: Event) => void;
};

const CompetenceTable: React.FC<CompetenceTableProps> = (
	props: CompetenceTableProps
) => {
	const { t } = useTranslation();

	function handleChange(index: number, event: Event): void {
		event.applyContext("/" + index);
		props.onChange(event);
	}

	function handleInsert(criterion: Competence): void {
		const path = "/" + props.elements.length;
		props.onInsert(new AddEvent(path, criterion));
	}

	function getRows(): JSX.Element[] {
		const elements: JSX.Element[] = [];
		for (const [index, element] of props.elements.entries()) {
			if (props.listInfo.isVisible(element.uuid)) {
				elements.push(
					<CompetenceRow
						key={element.uuid}
						index={index}
						criterion={element}
						editable={props.editable}
						labels={props.labels}
						selected={props.listInfo.isSelected(element.uuid)}
						onSelect={select =>
							props.onSelect([element.uuid], select)
						}
						onChange={event => handleChange(index, event)}
					/>
				);
			}
		}

		return elements;
	}

	return (
		<Table>
			<TableHead>
				<TableRow>
					<TableCell
						id="select-header"
						align="center"
						padding="checkbox">
						<Checkbox
							id="select-all"
							disabled={!props.editable}
							checked={props.listInfo.allVisibleSelected()}
							indeterminate={props.listInfo.someVisibleSelected()}
							onChange={(_event, checked) =>
								props.onSelect(
									props.listInfo.getVisibleKeys(),
									checked
								)
							}
						/>
					</TableCell>
					<TableCell id="number-header" sx={{ width: "6ch" }}>
						#
					</TableCell>
					<TableCell id="expand-header" sx={{ width: "6ch" }} />
					<TableCell id="name-header">{asTitle(t("name"))}</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{getRows()}
				{props.editable && (
					<NewCompetenceRow
						key={props.elements.length}
						onCreate={handleInsert}
					/>
				)}
			</TableBody>
		</Table>
	);
};

export default CompetenceTable;
